import * as React from 'react';
import { useEffect, useContext, useState } from 'react';
import {
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../store/userContext';
import { SiteContext } from '../store/siteContext';
import { StorageContext } from '../store/storageContext';
import { ReportContext } from '../store/reportContext';
import useGetSiteName from '../utils/hooks/useGetSiteName';
import loadingUsers from './loadingUsers';

import UserHeader from './UserHeader';
import UserColumn from './UserColumn';
import SiteColumn from './SiteColumn';
import StorageColumn from './StorageColumn';
import ReportTypeColumn from './ReportTypeColumn';

const UserList: React.FC = () => {
  const {
    getUsers,
    users,
    getUsersLoading,
    editUser,
  } = useContext(UserContext);
  const { getReports, reports, getReportsLoading } = useContext(ReportContext);
  const { getSite, sites } = useContext(SiteContext);
  const { storages } = useContext(StorageContext);
  const { getSiteName } = useGetSiteName();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [selectedStorageId, setSelectedStorageId] = useState<number | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const theme = useTheme();
  const { t } = useTranslation('nav');


    useEffect(() => {
    getUsers();
  }, [getUsers]);

  const filteredUsers = (getUsersLoading ? loadingUsers : users).filter((user) =>
    user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const handleUserClick = (user: any) => {
    setSelectedUserId(user.id);
    setSelectedSiteId(null);
    setSelectedStorageId(null);
    user.siteIds?.forEach((siteId: number) => getSite(siteId));
  };

  const handleSiteClick = (siteId: number) => {
    setSelectedSiteId(siteId);
    setSelectedStorageId(null);
  };

  const handleStorageClick = (storageId: number) => {
    setSelectedStorageId(storageId);
    if (selectedUserId) {
      getReports(false, { userId: selectedUserId, storageId });
    }
  };

  const handleDeleteSite = (siteId: number) => {
    const selectedUser = users.find((u) => u.id === selectedUserId);
    if (selectedUser) {
      const updatedSiteIds = selectedUser.siteIds?.filter((id) => id !== siteId) || [];
      const updatedUser: UserFormDto = {
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedUser.role,
        locale: selectedUser.locale,
        siteIds: updatedSiteIds,
      };
      editUser(selectedUser.id, updatedUser, () => {
        getUsers(true);
        setSelectedSiteId(null);
        setSelectedStorageId(null);
      });
    }
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const selectedSite = sites.find((site) => site.id === selectedSiteId);
  const filteredSiteIds = selectedUser?.siteIds?.filter((siteId) =>
    getSiteName(siteId).toLowerCase().includes(siteSearchTerm.toLowerCase())
  );
  const connectedStorages = storages.filter((storage) =>
    selectedSite?.storageIds?.includes(storage.id)
  );

  return (
    <Container maxWidth="lg">
      <Typography
        align="left"
        variant="h3"
        sx={(theme) => ({
          ...theme.typography.listTitle,
          color: theme.palette.titleColor,
          [theme.breakpoints.down('md')]: {
            fontSize: '2.5rem',
          },
        })}
      >
        {t('t.users')}
      </Typography>
      <Grid container alignItems="stretch" sx={{ border: theme.palette.border }}>
        <UserHeader
          userSearchTerm={userSearchTerm}
          setUserSearchTerm={setUserSearchTerm}
          siteSearchTerm={siteSearchTerm}
          setSiteSearchTerm={setSiteSearchTerm}
          selectedUser={selectedUser}
        />

        <Grid container size={{ xs: 12 }} alignItems="stretch">
          <UserColumn
            filteredUsers={filteredUsers}
            selectedUserId={selectedUserId}
            onUserClick={handleUserClick}
          />

          <SiteColumn
            selectedUserId={selectedUserId}
            selectedSiteId={selectedSiteId}
            siteIds={filteredSiteIds}
            getSiteName={getSiteName}
            onSiteClick={handleSiteClick}
            onDeleteSite={handleDeleteSite}
          />

          <StorageColumn
            selectedUserId={selectedUserId}
            selectedSiteId={selectedSiteId}
            selectedStorageId={selectedStorageId}
            storages={connectedStorages}
            onStorageClick={handleStorageClick}
          />

          <ReportTypeColumn
            selectedUserId={selectedUserId}
            selectedStorageId={selectedStorageId}
            getReportsLoading={getReportsLoading}
            reports={reports}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserList;
